import './CensusUploadStep.scss';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as DownloadIcon } from '../../../../../assets/icons/icon-download.svg';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import { TRANSLATIONS_QUOTES } from '../../../../translations/quotes';
import CustomButton from '../../../Buttons/CustomButton/CustomButton';
import FileDropzone from '../FileDropzone/FileDropzone';

export interface CensusInstruction {
  title: string;
  sub?: string;
}

export interface CensusUploadStepProps {
  file: File | null;
  onFileSelect: (file: File) => void;
  onClear: () => void;
  onDownloadTemplate: () => Promise<void>;
  acceptedFormats?: string[];
  onToggleInstructions?: () => void;
  instructions?: CensusInstruction[];
}

export default function CensusUploadStep({
  file,
  onFileSelect,
  onClear,
  onDownloadTemplate,
  acceptedFormats = ['.csv', '.xlsx', '.xls'],
  onToggleInstructions,
  instructions
}: Readonly<CensusUploadStepProps>) {
  const { t } = useTranslation();
  const [showInstructions, setShowInstructions] = useState(false);

  const INSTRUCTIONS: CensusInstruction[] = instructions ?? [
    {
      title: t(TRANSLATIONS_QUOTES.create.censusModal.instruction1Title),
      sub: t(TRANSLATIONS_QUOTES.create.censusModal.instruction1Sub)
    },
    {
      title: t(TRANSLATIONS_QUOTES.create.censusModal.instruction2Title),
      sub: t(TRANSLATIONS_QUOTES.create.censusModal.instruction2Sub)
    },
    {
      title: t(TRANSLATIONS_QUOTES.create.censusModal.instruction3Title),
      sub: t(TRANSLATIONS_QUOTES.create.censusModal.instruction3Sub)
    }
  ];

  return (
    <div className="census-step">
      <FileDropzone
        file={file}
        onFileSelect={onFileSelect}
        onClear={onClear}
        accept={acceptedFormats.join(',')}
        label={t(TRANSLATIONS_QUOTES.create.censusModal.dropzone)}
        orLabel={t(TRANSLATIONS_GLOBAL.or.main)}
        browseLabel={t(TRANSLATIONS_GLOBAL.button.browseFiles)}
        caption={`${t(TRANSLATIONS_QUOTES.create.censusModal.formats)} ${acceptedFormats.join(' · ')}`}
      />

      <div className="census-upload-actions">
        <CustomButton variant="default" size="xs" onClick={onDownloadTemplate}>
          <DownloadIcon />
          {t(TRANSLATIONS_QUOTES.create.censusModal.downloadTemplate)}
        </CustomButton>
        <button
          type="button"
          className="census-upload-toggle"
          onClick={() => {
            setShowInstructions((v) => !v);
            onToggleInstructions?.();
          }}
        >
          {showInstructions ? '▾' : '▸'} {t(TRANSLATIONS_QUOTES.create.censusModal.howDoesItWork)}
        </button>
      </div>

      {showInstructions && (
        <div className="census-upload-instructions">
          {INSTRUCTIONS.map(({ title, sub }, index) => (
            <div key={title} className="census-upload-instructions__step">
              <div className="census-upload-instructions__step-num">{index + 1}</div>
              <div>
                <div className="census-upload-instructions__step-title">{title}</div>
                {sub && <div className="census-upload-instructions__step-sub">{sub}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
